import { EyeOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useHistory } from 'react-router-dom';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import UserContext from '../../context/user-context';
import {
  errorHandler,
  estadosPoliza,
  exportCSVFile,
  getTagColorByEstado,
  tiposSolicitud,
  toUpperCase
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import { formatoMonedaGenerico } from '../../utils/converters';
import { fieldTypes, filterConditions } from '../../utils/filter-utils';

function PolizaList() {
  const tableRef = useRef();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState();
  const [visiblePolizaMasiva, setVisiblePolizaMasiva] = useState(false);
  const [formPolizaMasiva] = Form.useForm();
  const [loadingMasiva, setLoadingMasiva] = useState(false);
  const [columns, setColumns] = useState([]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const history = useHistory();
  const [defaultFilters, setDefaultFilters] = useState({
    id_poliza_masiva: {
      value: 'null',
      filterType: fieldTypes.STRING,
      filterCondition: filterConditions.EQUALS
    }
  });

  const getExportData = async (params) => {
    try {
      const response = await instanceRef.instance.get('/poliza', {
        params: {
          ...params,
          pageAll: true
        }
      });
      if (response?.data?.data?.rows) {
        const polizas = response?.data?.data?.rows;
        const itemsFormatted = [];

        const encabezado = {
          numero: 'numero',
          fecha_emision: 'emision',
          cliente: 'cliente',
          nro_documento: 'cedula',
          producto: 'producto',
          aseguradora: 'aseguradora',
          ejecutivo: 'ejecutivo',
          sucusal: 'sucursal',
          estado: 'estado',
          premio: 'premio',
          nro_factura: 'factura',
          tipo: 'tipo',
          lote: 'lote',
          sebaot_link_documentos: 'link'
        };
        polizas.forEach((item) => {
          const solicitante = JSON.parse(item.solicitud.solicitante);
          const {
            aseguradoraEmpresa,
            usuarioVendedor
          } = item.solicitud?.cotizacion;
          itemsFormatted.push({
            numero: getNumeroPoliza(item),
            fecha_emision: moment(item.fecha_emision).format('DD/MM/YYYY'),
            cliente: solicitante.persona?.nombres.replace(/,/g, ''),
            nro_documento: solicitante.persona?.cedula,
            producto: item.producto.descripcion,
            aseguradora: aseguradoraEmpresa?.aseguradora?.descripcion,
            ejecutivo: usuarioVendedor
              ? usuarioVendedor.nombres
              : 'Sin Ejecutivo',
            sucusal:
              usuarioVendedor && usuarioVendedor.sucursal
                ? usuarioVendedor.sucursal.descripcion
                : '',
            estado: item.estado,
            premio: item.premio,
            nro_factura: item.nro_factura,
            tipo: item.solicitud.tipo,
            lote: item.id_poliza_masiva || '',
            sebaot_link_documentos: item.sebaot_link_documentos || ''
          });
        });
        // call the exportCSVFile() function to process the JSON and trigger the download
        exportCSVFile(encabezado, itemsFormatted, 'Polizas');
      }
    } catch (error) {
      // error
    }
  };

  const customFilters = (filtersTable) => {
    if (filtersTable.estado) {
      const tieneEstadoPendiente = filtersTable.estado.find(
        (item) => item === 'PENDIENTE'
      );
      if (tieneEstadoPendiente) {
        return {
          ...filtersTable,
          estado: [...filtersTable.estado, ...['CREADO', 'APROBADO']].filter(
            (item) => item !== 'PENDIENTE'
          )
        };
      }
      return {
        ...filtersTable,
        estado: filtersTable.estado.filter(
          (item) => item !== 'APROBADO' && item !== 'CREADO'
        )
      };
    }
    return filtersTable;
  };

  const showModalPolizaMasiva = () => {
    setVisiblePolizaMasiva(true);
  };

  const handleCancelPolizaMasiva = async () => {
    setVisiblePolizaMasiva(false);
  };

  const handleOkPolizaMasiva = async () => {
    setLoadingMasiva(true);
    try {
      const nroLote = formPolizaMasiva.getFieldValue('numero_lote');
      await instanceRef.instance.post(`proceso/generarLinkPolizaMasiva`, {
        polizaMasivaId: nroLote
      });
      message.success('Link Póliza generada');
      setVisiblePolizaMasiva(false);
      setLoadingMasiva(false);
    } catch (error) {
      setLoadingMasiva(false);
      errorHandler(error);
    }
  };

  const getNumeroPoliza = (fields) => {
    return fields?.numero
      ? `${fields.seccion || ''}-${fields.numero || ''}-${
          fields.endoso === null ? '' : fields.endoso
        }`
      : 'SIN NÚMERO';
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  useEffect(() => {
    let columnas;
    const esAdminAseguradora =
      user && user['cognito:groups'][0] === 'ADMIN_ASEGURADORA';
    const esAdmin = user && user['cognito:groups'][0] === 'ADMIN';
    if (esAdminAseguradora) {
      columnas = [
        {
          title: 'Corredora',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'empresa',
            'ec',
            'nombre'
          ],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Empresa',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'empresa',
            'nombre'
          ],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Número',
          // dataIndex: 'numero',
          filterType: fieldTypes.STRING,
          sortable: true,
          render: (record) => {
            return getNumeroPoliza(record);
          }
        },
        {
          title: 'Emisión',
          dataIndex: 'fecha_emision',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '20%'
        },

        {
          title: 'Cliente',
          dataIndex: ['solicitud', 'solicitante'],
          key: 'solicitud.solicitante',
          render: (solicitante) => {
            if (solicitante) {
              const poliza = JSON.parse(solicitante);
              const nombres = poliza?.empresaJuridica
                ? `${poliza?.empresaJuridica?.razon}`
                : `${poliza?.persona?.nombres}`;
              return nombres;
            }
            return '-';
          },
          width: '20%'
        },
        {
          title: 'Cédula',
          dataIndex: ['solicitud', 'solicitante'],
          key: 'solicitud.solicitante',
          render: (solicitante) => {
            if (solicitante) {
              const poliza = JSON.parse(solicitante);
              const cedula = poliza?.empresaJuridica
                ? `${poliza?.empresaJuridica?.ruc}`
                : `${poliza?.persona?.cedula}`;
              return cedula;
            }
            return '-';
          },
          width: '20%'
        },
        {
          title: 'Producto',
          dataIndex: ['producto', 'descripcion'],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Estado',
          dataIndex: 'estado',
          filterType: fieldTypes.ENUM,
          filters: Object.keys(estadosPoliza).map((key) => ({
            text: estadosPoliza[key],
            value: key
          })),
          sortable: true,
          render: (estado) => {
            let estadoUpperCase = toUpperCase(estado);
            let mensaje = '';
            if (
              estadoUpperCase === 'CREADO' ||
              estadoUpperCase === 'APROBADO'
            ) {
              estadoUpperCase = 'PENDIENTE';
              mensaje = 'Pendiente de Póliza digitalizada';
            }
            return (
              <Tooltip placement="top" title={mensaje}>
                <Tag
                  color={getTagColorByEstado(estadoUpperCase, user)}
                  key={estadoUpperCase}
                >
                  {estadoUpperCase}
                </Tag>
              </Tooltip>
            );
          }
        },
        {
          title: 'Premio',
          dataIndex: 'premio',
          filterType: fieldTypes.NUMERIC,
          sortable: true,
          render: (premio, record) => (
            <div style={{ whiteSpace: 'nowrap' }}>
              {formatoMonedaGenerico(record.moneda, premio)}
            </div>
          )
        },
        {
          title: 'Factura',
          dataIndex: 'nro_factura',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Tipo',
          dataIndex: ['solicitud', 'tipo'],
          filterType: fieldTypes.ENUM,
          width: '20%',
          sortable: true,
          filters: Object.keys(tiposSolicitud).map((key) => ({
            text: tiposSolicitud[key],
            value: key
          }))
        },
        {
          title: 'Lote Masiva',
          dataIndex: 'id_poliza_masiva',
          filterType: fieldTypes.NUMERIC,
          sortable: true
        },
        {
          title: 'Acciones',
          key: 'action',
          width: '10px',
          align: 'left',
          render: (text, record) => (
            <Space size="small">
              <Tooltip title="Ver póliza">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="middle"
                  onClick={() => handleOpenUrl(`polizas/${record.token}`)}
                />
              </Tooltip>
              {record.estado === 'APROBADO' && !record.id_poliza_masiva && (
                <Tooltip title="Generar Póliza y Factura">
                  <Button
                    type="text"
                    shape="circle"
                    onClick={async () => {
                      setLoading(true);
                      let link = record.sebaot_link_documentos;
                      if (!link) {
                        try {
                          const response = await instanceRef.instance.get(
                            `poliza/link/${record.token}`
                          );
                          if (response.data.data) {
                            link = response.data.data;
                          } else {
                            message.warning(
                              'Póliza y Factura en Proceso de Emisión'
                            );
                            setLoading(false);
                            return;
                          }
                        } catch (error) {
                          message.warning(
                            'Póliza y Factura en Proceso de Emisión'
                          );
                          setLoading(false);
                          return;
                        }
                      }
                      setLoading(false);
                      window.open(`${link}`, '_blank');
                    }}
                    icon={<ReloadOutlined />}
                    size="middle"
                    loading={loading}
                  />
                </Tooltip>
              )}
            </Space>
          )
        }
      ];
    } else {
      columnas = [
        {
          title: 'Número',
          key: 'numero',
          filterType: fieldTypes.STRING,
          sortable: true,
          render: (record) => {
            return getNumeroPoliza(record);
          }
        },
        {
          title: 'Emisión',
          dataIndex: 'fecha_emision',
          filterType: fieldTypes.DATETIME,
          sortable: true,
          width: '20%'
        },

        {
          title: 'Cliente',
          dataIndex: ['solicitud', 'solicitante'],
          key: 'solicitud.solicitante',
          render: (solicitante) => {
            if (solicitante) {
              const poliza = JSON.parse(solicitante);
              const nombres = poliza?.empresaJuridica
                ? `${poliza?.empresaJuridica?.razon}`
                : `${poliza?.persona?.nombres}`;
              return nombres;
            }
            return '-';
          },
          width: '20%'
        },

        {
          title: 'Cédula',
          dataIndex: ['solicitud', 'solicitante'],
          key: 'solicitud.solicitante',
          render: (solicitante) => {
            if (solicitante) {
              const poliza = JSON.parse(solicitante);
              const cedula = poliza?.empresaJuridica
                ? `${poliza?.empresaJuridica?.ruc}`
                : `${poliza?.persona?.cedula}`;
              return cedula;
            }
            return '-';
          },
          width: '20%'
        },

        {
          title: 'Producto',
          dataIndex: ['producto', 'descripcion'],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Aseguradora',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'aseguradoraEmpresa',
            'aseguradora',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Ejecutivo',
          dataIndex: ['solicitud', 'cotizacion', 'usuarioVendedor'],
          key: 'solicitud.cotizacion.usuarioVendedor.nombres',
          render: (usuarioVendedor) => {
            let nombres = '';
            if (usuarioVendedor) {
              nombres = usuarioVendedor.nombres;
            }
            return (
              nombres || (
                <Typography.Text type="secondary">
                  Sin Ejecutivo
                </Typography.Text>
              )
            );
          },
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Sucursal',
          dataIndex: [
            'solicitud',
            'cotizacion',
            'usuarioVendedor',
            'sucursal',
            'descripcion'
          ],
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Estado',
          dataIndex: 'estado',
          filterType: fieldTypes.ENUM,
          filters: Object.keys(estadosPoliza).map((key) => {
            return {
              text: estadosPoliza[key],
              value: key
            };
          }),
          sortable: true,
          render: (estado) => {
            let estadoUpperCase = toUpperCase(estado);
            let mensaje = '';
            if (
              estadoUpperCase === 'CREADO' ||
              estadoUpperCase === 'APROBADO'
            ) {
              estadoUpperCase = 'PENDIENTE';
              mensaje = 'Pendiente de Póliza digitalizada';
            }
            if (estadoUpperCase === 'VIGENTE_PENDIENTE_DOCUMENTOS') {
              estadoUpperCase = 'DOCUMENTOS PENDIENTES';
              mensaje = 'Subir Póliza digitalizada';
            }
            return (
              <Tooltip placement="top" title={mensaje}>
                <Tag
                  color={getTagColorByEstado(estadoUpperCase, user)}
                  key={estadoUpperCase}
                >
                  {estadoUpperCase}
                </Tag>
              </Tooltip>
            );
          }
        },
        {
          title: 'Premio',
          dataIndex: 'premio',
          filterType: fieldTypes.NUMERIC,
          sortable: true,
          render: (premio, record) => (
            <div style={{ whiteSpace: 'nowrap' }}>
              {formatoMonedaGenerico(record.moneda, premio)}
            </div>
          )
        },
        {
          title: 'Factura',
          dataIndex: 'nro_factura',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Tipo',
          dataIndex: ['solicitud', 'tipo'],
          filterType: fieldTypes.ENUM,
          width: '20%',
          sortable: true,
          filters: Object.keys(tiposSolicitud).map((key) => ({
            text: tiposSolicitud[key],
            value: key
          }))
        },
        {
          title: 'Lote Masiva',
          dataIndex: 'id_poliza_masiva',
          filterType: fieldTypes.NUMERIC,
          sortable: true
        },
        {
          title: 'Acciones',
          key: 'action',
          width: '10px',
          align: 'left',
          render: (text, record) => (
            <Space size="small">
              <Tooltip title="Ver póliza">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="middle"
                  onClick={() => handleOpenUrl(`polizas/${record.token}`)}
                />
              </Tooltip>
              {record.estado === 'APROBADO' && !record.id_poliza_masiva && (
                <Tooltip title="Generar Póliza y Factura">
                  <Button
                    type="text"
                    shape="circle"
                    onClick={async () => {
                      setLoading(true);
                      let link = record.sebaot_link_documentos;
                      if (!link) {
                        try {
                          const response = await instanceRef.instance.get(
                            `poliza/link/${record.token}`
                          );
                          if (response.data.data) {
                            link = response.data.data;
                          } else {
                            message.warning(
                              'Póliza y Factura en Proceso de Emisión'
                            );
                            setLoading(false);
                            return;
                          }
                        } catch (error) {
                          message.warning(
                            'Póliza y Factura en Proceso de Emisión'
                          );
                          setLoading(false);
                          return;
                        }
                      }
                      setLoading(false);
                      window.open(`${link}`, '_blank');
                    }}
                    icon={<ReloadOutlined />}
                    size="middle"
                    loading={loading}
                  />
                </Tooltip>
              )}
              {record.estado === 'CREADO' && (
                <Tooltip title="Generar Póliza">
                  <Button
                    type="text"
                    shape="circle"
                    onClick={async () => {
                      try {
                        const reCaptchaToken = await executeRecaptcha(
                          'poliza/emision'
                        );
                        await instanceRef.instance.post(`poliza/emision`, {
                          reCaptchaToken,
                          token: record.token
                        });
                        message.info('Generando Póliza');
                        tableRef.current.reload();
                      } catch (error) {
                        message.error('Error al generar');
                      }
                    }}
                    icon={<ReloadOutlined />}
                    size="middle"
                  />
                </Tooltip>
              )}
              {(esAdminAseguradora || esAdmin) && record.firma_manual && (
                <Tooltip title="Subir documentos Póliza">
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() =>
                      handleOpenUrl(`poliza/subir/${record.token}`)
                    }
                    icon={<UploadOutlined />}
                    size="middle"
                    disabled={record.estado !== 'VIGENTE_PENDIENTE_DOCUMENTOS'}
                  />
                </Tooltip>
              )}
            </Space>
          )
        }
      ];
    }
    setColumns(columnas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, executeRecaptcha]);

  return (
    <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
      {user && user['cognito:groups'].includes('ADMIN') && (
        <Card style={{ marginBottom: 5, textAlign: 'left' }} title="Filtros">
          <Form
            initialValues={{ tipo: 'individual' }}
            scrollToFirstError
            layout="horizontal"
            name="historico"
            autoComplete="off"
            onValuesChange={(values) => {
              setDefaultFilters({
                id_poliza_masiva: {
                  value: 'null',
                  filterType: fieldTypes.STRING,
                  filterCondition:
                    values.tipo === 'individual'
                      ? filterConditions.EQUALS
                      : filterConditions.NOT_EQUALS
                }
              });
              setTimeout(() => {
                tableRef.current.reload();
              }, 500);
            }}
          >
            <Row justify="start">
              <Col lg={12} md={12} xs={24}>
                <Form.Item label="Tipo" name="tipo">
                  <Radio.Group>
                    <Radio.Button value="individual">Individual</Radio.Button>
                    <Radio.Button value="masivas">Por lote</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      )}
      <TableAndFilters
        ref={tableRef}
        withGeneralFilter
        defaultFilters={defaultFilters}
        url="/poliza"
        title="Pólizas"
        columns={columns}
        showTable
        hasExportar={user && user['cognito:groups'].includes('ADMIN')}
        getExportData={
          user && user['cognito:groups'].includes('ADMIN') && getExportData
        }
        extra={[
          user && user['cognito:groups'].includes('ADMIN') && (
            <Button
              key="poliza_masiva"
              type="dashed"
              shape="round"
              icon={<ReloadOutlined />}
              size="middle"
              onClick={() => showModalPolizaMasiva()}
              // style={styles.menuAction}
            >
              Generar Póliza Masiva
            </Button>
          )
        ]}
        customFilters={customFilters}
      />
      <Modal
        title="Generar Link Póliza Masiva"
        open={visiblePolizaMasiva}
        onOk={handleOkPolizaMasiva}
        onCancel={handleCancelPolizaMasiva}
        confirmLoading={loadingMasiva}
      >
        <Form
          form={formPolizaMasiva}
          layout="vertical"
          name="polizaMasivaForm"
          // style={styles.form}
        >
          <Form.Item
            label="Nro Lote"
            name="numero_lote"
            rules={[
              {
                required: true,
                message: 'Requerido'
              }
            ]}
          >
            <Input placeholder="Ingrese un valor" />
          </Form.Item>
        </Form>
      </Modal>
    </ResponsiveContainer>
  );
}

export default PolizaList;
